import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { toast } from 'react-toastify';
import { UserContext } from '../../UserContext';

const AcceptBasketPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [basketData, setBasketData] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  
  // Get user from UserContext instead of Redux
  const { user } = useContext(UserContext);
  
  // First, ensure user is loaded
  useEffect(() => {
    console.log('AcceptBasket: Checking user in context:', user);
    if (user) {
      setUserLoaded(true);
    }
  }, [user]);
  
  // Fetch workspaces and decode token once user is loaded
  useEffect(() => {
    if (!userLoaded) {
      console.log('AcceptBasket: Waiting for user to be loaded');
      return;
    }
    
    const loadData = async () => {
      console.log('AcceptBasket: loadData started with user:', user);
      const token = searchParams.get('token');
      console.log('AcceptBasket: token from URL:', token);
      
      if (!token) {
        console.log('AcceptBasket: No token found in URL');
        setStatus('error');
        setError('Invalid sharing link: Missing token');
        return;
      }
      
      try {
        // Decode token
        console.log('AcceptBasket: Decoding token');
        const decodedString = atob(token);
        const decoded = JSON.parse(decodedString);
        console.log('AcceptBasket: Decoded basket data:', decoded);
        setBasketData({ ...decoded, token });
        
        // Fetch user's workspaces
        console.log('AcceptBasket: Fetching workspaces for user ID:', user.id);
        const { data: userWorkspaces, error: workspaceError } = await supabase
          .from('workspaces')
          .select('*')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false });
        
        console.log('AcceptBasket: Workspaces response:', userWorkspaces, workspaceError);
        
        if (workspaceError) {
          console.error('AcceptBasket: Error fetching workspaces:', workspaceError);
          throw new Error('Failed to fetch workspaces');
        }
        
        if (userWorkspaces && userWorkspaces.length > 0) {
          console.log('AcceptBasket: Setting workspaces and default workspace');
          setWorkspaces(userWorkspaces);
          setSelectedWorkspace(userWorkspaces[0].id);
        } else {
          console.log('AcceptBasket: No workspaces found for user');
        }
        
        setStatus('ready');
        console.log('AcceptBasket: Status set to ready');
      } catch (err) {
        console.error('AcceptBasket: Error in loadData:', err);
        setStatus('error');
        setError(err.message || 'Failed to load data');
      }
    };
    
    console.log('AcceptBasket: useEffect triggered, calling loadData');
    loadData();
  }, [searchParams, navigate, user, userLoaded]);
  
  // Function to process basket creation
  const handleAcceptBasket = async () => {
    console.log('AcceptBasket: handleAcceptBasket called', { selectedWorkspace, basketData });
    if (!selectedWorkspace || !basketData) return;
    
    setStatus('processing');
    console.log('AcceptBasket: Status set to processing');
    
    try {
      console.log('AcceptBasket: Processing basket directly with Supabase');
      
      // 1. Create the basket - remove user_id as it's not in the schema
      const { data: basket, error: basketError } = await supabase
        .from('baskets')
        .insert([
          { 
            name: basketData.basketName,
            description: basketData.description || null,
            workspace_id: selectedWorkspace
            // user_id removed as it doesn't exist in the baskets table
          }
        ])
        .select();
      
      if (basketError) {
        console.error('AcceptBasket: Error creating basket:', basketError);
        throw new Error(`Failed to create basket: ${basketError.message}`);
      }
      
      const basketId = basket[0].id;
      console.log('AcceptBasket: Created basket with ID:', basketId);
      
      // 2. Process topics if available
      if (basketData.topics && basketData.topics.length > 0) {
        console.log(`AcceptBasket: Processing ${basketData.topics.length} topics`);
        
        for (const topicData of basketData.topics) {
          console.log('AcceptBasket: Processing topic:', topicData.name);
          
          // 2a. Handle tag if present
          let tagId = null;
          if (topicData.tag) {
            // Check if tag exists
            const { data: existingTags } = await supabase
              .from('tags')
              .select('id')
              .eq('name', topicData.tag.name)
              .eq('created_by', user.id);  // Using created_by instead of user_id
            
            if (existingTags && existingTags.length > 0) {
              // Use existing tag
              tagId = existingTags[0].id;
              console.log('AcceptBasket: Using existing tag:', tagId);
            } else {
              // Create new tag
              const { data: newTag, error: tagError } = await supabase
                .from('tags')
                .insert([
                  {
                    name: topicData.tag.name,
                    color: topicData.tag.color || 'bg-blue-100',
                    created_by: user.id  // Using created_by instead of user_id
                  }
                ])
                .select();
              
              if (tagError) {
                console.warn('AcceptBasket: Error creating tag:', tagError);
                // Continue without tag if there's an error
              } else {
                tagId = newTag[0].id;
                console.log('AcceptBasket: Created new tag:', tagId);
              }
            }
          }
          
          // 2b. Create topic
          const { data: topic, error: topicError } = await supabase
            .from('topics')
            .insert([
              {
                name: topicData.name,
                relation_description: topicData.relation_description || null,
                basket_id: basketId,
                workspace_id: selectedWorkspace,
                user_id: user.id,
                tag_id: tagId
              }
            ])
            .select();
          
          if (topicError) {
            console.error('AcceptBasket: Error creating topic:', topicError);
            continue; // Skip to next topic if there's an error
          }
          
          // 2c. Create concepts if present
          if (topicData.concepts && topicData.concepts.length > 0) {
            console.log(`AcceptBasket: Processing ${topicData.concepts.length} concepts`);
            
            for (const concept of topicData.concepts) {
              const { error: conceptError } = await supabase
                .from('concepts')
                .insert([
                  {
                    text: concept.text,
                    is_highlighted: concept.is_highlighted || false,
                    topic_id: topic[0].id,
                    order_index: concept.order_index || 0
                  }
                ]);
              
              if (conceptError) {
                console.warn('AcceptBasket: Error creating concept:', conceptError);
              }
            }
          }
        }
      }
      
      // Show success message
      toast.success('Basket successfully added to workspace!');
      
      // Redirect to workspace
      console.log('AcceptBasket: Redirecting to workspace with new basket ID:', basketId);
      navigate(`/workspace/${selectedWorkspace}`, { 
        state: { shouldScrollToBasket: basketId }
      });
    } catch (err) {
      console.error('AcceptBasket: Error in handleAcceptBasket:', err);
      setStatus('error');
      setError(err.message || 'Failed to add basket');
    }
  };
  
  // Function to create a new workspace
  const handleCreateWorkspace = async (workspaceName) => {
    if (!workspaceName) {
      workspaceName = prompt('Enter new workspace name:');
    }
    
    if (!workspaceName?.trim()) return;
    
    try {
      const { data: newWorkspace, error } = await supabase
        .from('workspaces')
        .insert([
          { name: workspaceName, user_id: user.id }
        ])
        .select();
      
      if (error) throw error;
      
      if (newWorkspace && newWorkspace.length > 0) {
        setWorkspaces([...workspaces, newWorkspace[0]]);
        setSelectedWorkspace(newWorkspace[0].id);
        toast.success('New workspace created!');
      }
    } catch (err) {
      toast.error('Failed to create workspace');
      console.error(err);
    }
  };
  
  // Loading state
  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50">
        <div className="w-16 h-16 border-t-4 border-cranberry-700 border-solid rounded-full animate-spin"></div>
        <h2 className="mt-4 text-xl font-semibold">Loading...</h2>
      </div>
    );
  }
  
  // Processing state
  if (status === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50">
        <div className="w-16 h-16 border-t-4 border-cranberry-700 border-solid rounded-full animate-spin"></div>
        <h2 className="mt-4 text-xl font-semibold">Adding basket to your workspace...</h2>
      </div>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50">
        <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl relative max-w-md">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
        <button 
          onClick={() => navigate('/')}
          className="mt-6 px-4 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }
  
  // Workspace selection UI
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-50 p-4">
      <div className="bg-white p-8 rounded-xl border-2 border-neutral-900 shadow-lg max-w-md w-full">
        <div className="flex items-center justify-center mb-6">
          <span className="text-3xl mr-3">🧺</span>
          <h1 className="text-2xl font-bold text-center">Accept Shared Basket</h1>
        </div>
        
        {basketData && (
          <div className="mb-6 bg-neutral-50 p-4 rounded-lg border border-neutral-200">
            <h2 className="text-xl font-semibold">{basketData.basketName}</h2>
            {basketData.description && (
              <p className="text-neutral-600 mt-1">{basketData.description}</p>
            )}
            <p className="text-sm text-neutral-500 mt-2">
              {basketData.topics?.length || 0} topics included
            </p>
          </div>
        )}
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Select a workspace to add this basket to:
          </label>
          
            <select
              value={selectedWorkspace || ''}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
            className="w-full p-3 border-2 border-neutral-300 rounded-lg focus:border-cranberry-500 focus:outline-none mb-4"
            >
            <option value="" disabled>Select a workspace</option>
              {workspaces.map(workspace => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          
          <div className="flex items-center space-x-2">
            <input 
              type="text" 
              id="newWorkspace"
              placeholder="Create new workspace"
              className="flex-1 p-3 border-2 border-neutral-300 rounded-lg focus:border-cranberry-500 focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  handleCreateWorkspace(e.target.value.trim());
                  e.target.value = '';
                }
              }}
            />
          <button 
              onClick={() => {
                const input = document.getElementById('newWorkspace');
                if (input.value.trim()) {
                  handleCreateWorkspace(input.value.trim());
                  input.value = '';
                }
              }}
              className="p-3 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors"
              aria-label="Create workspace"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
          </button>
          </div>
        </div>
        
        <div className="flex justify-between space-x-4 mt-8">
          <button
            onClick={() => navigate('/')}
            className="px-5 py-3 bg-white text-neutral-900 rounded-xl border-2 border-neutral-900 font-medium hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleAcceptBasket}
            disabled={!selectedWorkspace}
            className={`px-5 py-3 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors ${
              !selectedWorkspace ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Save to Workspace
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptBasketPage; 